import L from 'leaflet';
import {
  collectPoints,
  geometryCenter,
  type SuggestionGeometry,
} from '@/components/place-suggestions/geometry';

// Leaflet rendering for place-suggestion polygons + numbered pins on the
// Trackers map. Kept separate from DeviceMap so the geometry → layer mapping
// stays small and self-contained.

// gpx_terracotta-300 / -100 — matches the rank badge on the suggestion card.
const PIN_COLOR = '#E55852';
const PIN_COLOR_FOCUSED = '#E0352E';

export type SuggestionLayerEntry = {
  id: string;
  rank: number;
  geometry: SuggestionGeometry;
};

/**
 * A circular numbered pin matching a suggestion card's rank badge. The focused
 * pin is larger, darker, and ringed so the clicked card stands out on the map.
 */
export function createNumberedIcon(rank: number, focused: boolean): L.DivIcon {
  const size = focused ? 34 : 26;
  const fontSize = focused ? 15 : 12;
  const background = focused ? PIN_COLOR_FOCUSED : PIN_COLOR;
  const ring = focused ? ', 0 0 0 4px rgba(224,53,46,0.30)' : '';
  const style = [
    `width:${size}px`,
    `height:${size}px`,
    'border-radius:9999px',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    `background:${background}`,
    'color:#fff',
    'font-weight:700',
    `font-size:${fontSize}px`,
    'line-height:1',
    'border:2px solid #fff',
    `box-shadow:0 1px 4px rgba(0,0,0,0.4)${ring}`,
  ].join(';');
  return L.divIcon({
    className: 'gpx-suggestion-pin',
    html: `<div style="${style}">${rank}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/** Reverse GeoJSON `[lng, lat]` rings into the `[lat, lng]` Leaflet wants. */
function toLeafletLatLngs(geom: SuggestionGeometry): L.LatLngExpression[][][] {
  const reverseRing = (ring: number[][]): L.LatLngExpression[] =>
    ring.map(([lng, lat]) => [lat, lng] as L.LatLngExpression);
  if (geom.type === 'Polygon') {
    return [geom.coordinates.map(reverseRing)];
  }
  return geom.coordinates.map((poly) => poly.map(reverseRing));
}

/**
 * Redraw every suggestion polygon + pin into `group`. Existing layers are
 * cleared first, so this is safe to call on every entry/focus change.
 * `onSelect` fires when a polygon or pin is clicked.
 */
export function renderSuggestionEntries(
  group: L.LayerGroup,
  entries: SuggestionLayerEntry[],
  focusedId: string | null,
  onSelect: (id: string) => void,
): void {
  group.clearLayers();

  for (const entry of entries) {
    const focused = entry.id === focusedId;

    const polygon = L.polygon(toLeafletLatLngs(entry.geometry), {
      color: focused ? PIN_COLOR_FOCUSED : PIN_COLOR,
      weight: focused ? 3 : 2,
      opacity: 0.9,
      fillColor: PIN_COLOR,
      fillOpacity: focused ? 0.25 : 0.12,
    });
    polygon.on('click', () => onSelect(entry.id));
    polygon.addTo(group);

    const center = geometryCenter(entry.geometry);
    if (center) {
      const marker = L.marker(center, {
        icon: createNumberedIcon(entry.rank, focused),
        zIndexOffset: focused ? 1000 : 0,
        keyboard: false,
      });
      marker.on('click', () => onSelect(entry.id));
      marker.addTo(group);
    }
  }
}

/**
 * Combined Leaflet bounds covering every entry's geometry, or `null` when
 * there are no usable points. Used to fit-bounds the map to the suggestion set
 * (pass all entries) or to a single suggestion (pass a one-element array).
 */
export function suggestionEntriesBounds(
  entries: SuggestionLayerEntry[],
): L.LatLngBounds | null {
  const points: L.LatLngExpression[] = [];
  for (const entry of entries) {
    for (const [lng, lat] of collectPoints(entry.geometry)) {
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        points.push([lat, lng]);
      }
    }
  }
  if (points.length === 0) return null;
  return L.latLngBounds(points);
}
